/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback } from "react";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Alert,
  Breadcrumbs as MuiBreadcrumbs,
  Button,
  Grid,
  IconButton,
  Link,
  Paper as MuiPaper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Toolbar,
  Tooltip,
  Typography,
  Chip,
  Badge,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  FilterList as FilterListIcon,
} from "@mui/icons-material";
import { spacing } from "@mui/system";
import styled from "styled-components/macro";
import { withStyles } from "@mui/styles";
import moment from "moment";
import swal from "sweetalert";
import axios from "../../CustomAxios";
import Loading from "../components/Loading";
import { connect } from "react-redux";
import Filter from "./Filter"; // new filter modal

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);

const StyledTableContainer = withStyles(() => ({
  root: { width: "max-content" },
}))(TableContainer);

const Spacer = styled.div`flex: 1 1 100%;`;
const ToolbarTitle = styled.div`min-width: 150px;`;

const headCells = [
  { id: "customer_company", label: "Company Name" },
  { id: "plate", label: "Plate Details" },
  { id: "baking_cost", label: "Baking Cost" },
  { id: "print_cost", label: "Print Cost" },
  { id: "selling_cost", label: "Selling Cost" },
  { id: "gripper", label: "Gripper" },
  { id: "effective_date", label: "Effective Date" },
  { id: "status", label: "Status" },
];

// --------------------------------------------------
// getSchedules – keep this OUTSIDE the component
// --------------------------------------------------
const getSchedules = async (
  authData,
  setLoader,
  setRows,
  search,
  page,
  limit,
  setTotal,
  sortingField = "effective_date",
  sortingOrder = "desc",
  status = "all",
  companyId = "",
  plateId = "",
  fromDate = "",
  toDate = ""
) => {
  try {
    setLoader(true);
    const config = { headers: { Authorization: `Bearer ${authData.access_token}` } };
    let url = `/api/v1/${authData.type}/plate_price_schedules?page=${page + 1
      }&limit=${limit}&sorting_field=${sortingField}&sorting_order=${sortingOrder}`;

    if (search) url += `&search_keyword=${search}`;
    if (status !== "all") url += `&status=${status}`;
    if (companyId) url += `&company_id=${companyId}`;
    if (plateId) url += `&plate_id=${plateId}`;
    if (fromDate) url += `&from_date=${fromDate}`;
    if (toDate) url += `&to_date=${toDate}`;

    const { data } = await axios.get(url, config);
    if (data.success) {
      const schedules = data.data.data;
      setTotal(data.data.metadata.total_results);
      const rows = schedules.map((s) => ({
        id: s._id,
        customerCompany: s.customer_company?.name || "N/A",
        plate: s.plate
          ? `${s.plate.height}x${s.plate.width}x${s.plate.thickness} - ${s.plate.brand?.name || ""
          } (${s.plate.technology?.name || ""})`
          : "N/A",
        bakingCost: s.baking_cost,
        printCost: s.print_cost,
        sellingCost: s.selling_cost,
        gripper: s.gripper,
        effectiveDate: moment(s.effective_date).format("DD/MM/YYYY"),
        isActive: s.is_active && new Date(s.effective_date) <= new Date(),
        rawDate: s.effective_date,
      }));
      setRows(rows);
    }
    setLoader(false);
  } catch (error) {
    console.log(error);
    setLoader(false);
  }
};

// --------------------------------------------------
// Sub-components
// --------------------------------------------------
const EnhancedTableHead = ({ editPermission, deletePermission }) => (
  <TableHead>
    <TableRow>
      {headCells.map((hc) => (
        <TableCell key={hc.id}>{hc.label}</TableCell>
      ))}
      {editPermission && <TableCell align="center">Edit</TableCell>}
      {deletePermission && <TableCell align="center">Action</TableCell>}
    </TableRow>
  </TableHead>
);

const EnhancedTableToolbar = ({ filterCount, onFilterClick, navigate }) => (
  <Toolbar>
    <ToolbarTitle>
      <Typography variant="h6">Effective Cost</Typography>
    </ToolbarTitle>
    <Spacer />
    <Tooltip title="Filter list">
      <Badge badgeContent={filterCount} color="error">
        <IconButton onClick={onFilterClick} size="small">
          <FilterListIcon />
        </IconButton>
      </Badge>
    </Tooltip>
    <Tooltip title="Create Schedule">
      <IconButton onClick={() => navigate("/dashboard/plate-price-schedules/create")}>
        <AddIcon />
      </IconButton>
    </Tooltip>
  </Toolbar>
);

// --------------------------------------------------
// Main Component
// --------------------------------------------------
function PlatePriceScheduleList({ auths }) {
  const authData = JSON.parse(localStorage.getItem("AuthData"));
  const [loader, setLoader] = useState(true);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [total, setTotal] = useState(0);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [editPermission, setEditPermission] = useState(false);
  const [deletePermission, setDeletePermission] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Filter states – initial values from URL
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [status, setStatus] = useState(searchParams.get("status") || "all");
  const [companyId, setCompanyId] = useState(searchParams.get("company_id") || "");
  const [plateId, setPlateId] = useState(searchParams.get("plate_id") || "");
  const [fromDate, setFromDate] = useState(searchParams.get("from_date") || "");
  const [toDate, setToDate] = useState(searchParams.get("to_date") || "");

  const [filterOpen, setFilterOpen] = useState(false);
  const [filterCount, setFilterCount] = useState(0);

  // Compute filter badge count
  useEffect(() => {
    let count = 0;
    if (search) count++;
    if (status !== "all") count++;
    if (companyId) count++;
    if (plateId) count++;
    if (fromDate) count++;
    if (toDate) count++;
    setFilterCount(count);
  }, [search, status, companyId, plateId, fromDate, toDate]);

  // Permissions
  useEffect(() => {
    if (
      authData.type === "super_admin" ||
      auths?.user?.role?.permissions.includes("manage_plate_price_schedules")
    ) {
      setEditPermission(true);
      setDeletePermission(true);
    }
  }, [auths, authData.type]);

  // Fetch data
  const fetchData = useCallback(() => {
    getSchedules(
      authData,
      setLoader,
      setRows,
      search,
      page,
      rowsPerPage,
      setTotal,
      "effective_date",
      "desc",
      status,
      companyId,
      plateId,
      fromDate,
      toDate
    );
  }, [search, page, rowsPerPage, status, companyId, plateId, fromDate, toDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Sync URL query string
  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (status !== "all") params.set("status", status);
    if (companyId) params.set("company_id", companyId);
    if (plateId) params.set("plate_id", plateId);
    if (fromDate) params.set("from_date", fromDate);
    if (toDate) params.set("to_date", toDate);
    params.set("page", page);
    params.set("limit", rowsPerPage);
    navigate(`/dashboard/plate-price-schedules?${params.toString()}`, { replace: true });
  }, [search, status, companyId, plateId, fromDate, toDate, page, rowsPerPage, navigate]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleToggleActive = async (id, currentlyActive) => {
    const action = currentlyActive ? "deactivate" : "activate";
    const willDo = await swal({
      title: "Are you sure?",
      text: `You want to ${action} this schedule?`,
      icon: "warning",
      buttons: ["Cancel", "Yes"],
      dangerMode: true,
    });
    if (!willDo) return;
    try {
      const config = { headers: { Authorization: `Bearer ${authData.access_token}` } };
      await axios.put(
        `/api/v1/${authData.type}/plate-price-schedules/${id}`,
        { is_active: !currentlyActive },
        config
      );
      setSnackMessage(`Schedule ${action}d successfully`);
      setSnackOpen(true);
      fetchData();
    } catch (error) {
      console.log(error);
    }
  };

  const handleApplyFilters = (filters) => {
    setSearch(filters.search || "");
    setStatus(filters.status || "all");
    setCompanyId(filters.companyId || "");
    setPlateId(filters.plateId || "");
    setFromDate(filters.fromDate || "");
    setToDate(filters.toDate || "");
    setPage(0);
    setFilterOpen(false);
  };

  return (
    <>
      <Helmet title="Effective Cost" />
      <Grid container justifyContent="space-between" alignItems="center" spacing={10} mb={4}>
        <Grid item>
          <Typography variant="h4" gutterBottom display="inline" sx={{ fontWeight: 600 }} color="primary">
            PLATE PRICE SCHEDULES
          </Typography>
        </Grid>
        <Grid item>
          <Breadcrumbs>
            <Link component={NavLink} to="/dashboard">Dashboard</Link>
            <Typography>Effective Cost</Typography>
          </Breadcrumbs>
        </Grid>
      </Grid>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={snackOpen}
        autoHideDuration={4000}
        onClose={() => setSnackOpen(false)}
      >
        <Alert severity="success">{snackMessage}</Alert>
      </Snackbar>

      <Paper>
        <EnhancedTableToolbar
          filterCount={filterCount}
          onFilterClick={() => setFilterOpen(true)}
          navigate={navigate}
        />
        {!loader ? (
          <>
            <StyledTableContainer>
              <Table size="small">
                <EnhancedTableHead editPermission={editPermission} deletePermission={deletePermission} />
                <TableBody>
                  {rows.map((row, index) => (
                    <TableRow
                      key={`${row.id}-${index}`}
                      sx={{ backgroundColor: row.isActive ? "#fff" : "#f5f5f5" }}
                    >
                      <TableCell>{row.customerCompany}</TableCell>
                      <TableCell>{row.plate}</TableCell>
                      <TableCell>{row.bakingCost}</TableCell>
                      <TableCell>{row.printCost}</TableCell>
                      <TableCell>{row.sellingCost}</TableCell>
                      <TableCell>{row.gripper}</TableCell>
                      <TableCell>{row.effectiveDate}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.isActive ? "Active" : "Inactive"}
                          style={{ backgroundColor: row.isActive ? "#4caf50" : "#9e9e9e", color: "#fff" }}
                        />
                      </TableCell>
                      {editPermission && (
                        <TableCell align="center">
                          <Tooltip title="Edit Schedule">
                            <IconButton
                              onClick={() => navigate(`/dashboard/plate-price-schedules/edit/${row.id}`)}
                            >
                              <EditIcon color="primary" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                      {deletePermission && (
                        <TableCell align="center">
                          {row.isActive ? (
                            <Tooltip title="Deactivate Schedule">
                              <IconButton onClick={() => handleToggleActive(row.id, true)}>
                                <DeleteIcon color="error" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Activate Schedule">
                              <IconButton onClick={() => handleToggleActive(row.id, false)}>
                                <RestoreIcon color="success" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>
            <TablePagination
              rowsPerPageOptions={[50, 100, 250]}
              component="div"
              count={total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        ) : (
          <Loading />
        )}
      </Paper>

      <Filter
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApply={handleApplyFilters}
        initialValues={{ search, status, companyId, plateId, fromDate, toDate }}
      />
    </>
  );
}

const mapStateToProps = (state) => ({ auths: state.auths });
export default connect(mapStateToProps)(PlatePriceScheduleList);